if (!customElements.get('u5-interactive-feed')) {
  customElements.define('u5-interactive-feed', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.abort = new AbortController();
      this.signal = this.abort.signal;
      this.cards = Array.from(this.querySelectorAll('[data-video-card]'));
      this.track = this.querySelector('.u5-feed__track');
      this.dialog = this.querySelector('dialog');
      this.host = this.querySelector('[data-player-host]');
      this.labels = JSON.parse(this.querySelector('[data-u5-feed-labels]').textContent);
      this.status = this.querySelector('[data-player-status]');
      this.index = 0;
      this.autoEnabled = this.dataset.autoPlay === 'true';
      this.previewIndex = 0;
      this.isDocked = false;
      this.soundMuted = false;
      this.visibleCards = new Set();
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width: 767px)');
      this.wrapper = this.closest('.shopify-section');
      this.desktopMarker = document.createComment('U5 feed desktop location');
      this.before(this.desktopMarker);
      this.listen(this.mobile, 'change', () => this.place());
      this.listen(document, 'shopify:section:load', () => this.place());
      this.listen(document, 'shopify:section:unload', event => {
        if (event.target === this.wrapper) this.remove();
        else if (event.target.contains(this) && this.desktopMarker.isConnected) this.desktopMarker.after(this);
      });
      this.listen(this, 'click', event => this.onClick(event));
      this.listen(this, 'play', event => {
        if (event.target.tagName === 'VIDEO') this.pauseOthers(event.target);
      }, { capture: true });
      this.listen(this.track, 'scroll', () => this.updateArrows(), { passive: true });
      this.resize = new ResizeObserver(() => this.updateArrows());
      this.resize.observe(this.track);
      this.observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          const card = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio >= .6) this.visibleCards.add(card);
          else {
            this.visibleCards.delete(card);
            card.querySelector('video')?.pause();
          }
        });
        this.resumePreviews();
      }, { threshold: [.0, .6] });
      this.cards.forEach(card => this.observer.observe(card));
      this.listen(document, 'visibilitychange', () => {
        if (document.hidden) this.pauseAll();
        else if (!this.dialog.open) this.resumePreviews();
      });
      this.listen(this.dialog, 'close', () => this.onClose());
      this.listen(document, 'fullscreenchange', () => {
        if (!document.fullscreenElement) this.dialog.classList.remove('u5-feed__dialog--expanded');
      });
      this.listen(this.dialog, 'click', event => {
        if (event.target === this.dialog) {
          const box = this.dialog.getBoundingClientRect();
          if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
        }
      });
      this.listen(this.dialog, 'keydown', event => {
        if (event.key === 'Escape' && this.isDocked) { this.dialog.close(); return; }
        if (event.target.tagName === 'VIDEO') return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.showVideo(this.index + (event.key === 'ArrowRight' ? 1 : -1));
        }
      });
      this.listen(this.host, 'touchstart', event => {
        if (event.touches.length !== 1) return;
        const rect = this.host.getBoundingClientRect();
        this.touch = event.touches[0].clientY < rect.bottom - 70 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
      }, { passive: true });
      this.listen(this.host, 'touchend', event => {
        if (!this.touch || !event.changedTouches.length) return;
        const dx = event.changedTouches[0].clientX - this.touch.x;
        const dy = event.changedTouches[0].clientY - this.touch.y;
        if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.3) this.showVideo(this.index + (dx < 0 ? 1 : -1));
        this.touch = null;
      }, { passive: true });
      this.place();
      this.updateArrows();
    }
    listen(target, type, handler, options = {}) {
      target.addEventListener(type, handler, { ...options, signal: this.signal });
    }
    disconnectedCallback() {
      queueMicrotask(() => {
        if (this.isConnected) return;
        if (this.dialog.open) this.dialog.close();
        this.pauseAll();
        this.abort.abort();
        this.observer.disconnect();
        this.resize.disconnect();
        this.desktopMarker.remove();
        this.ready = false;
      });
    }
    place() {
      if (!this.wrapper || !this.desktopMarker.isConnected) return;
      const slot = document.querySelector('[data-u5-feed-mobile-slot]');
      if (this.mobile.matches && this.isDocked) this.setDocked(false);
      this.toggleAttribute('data-mobile-embedded', this.mobile.matches && Boolean(slot));
      if (this.mobile.matches && slot) {
        const anchor = slot.parentElement;
        if (this.previousSibling !== anchor) anchor.after(this);
      } else if (this.previousSibling !== this.desktopMarker) this.desktopMarker.after(this);
      this.updateArrows();
    }
    updateArrows() {
      const end = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      this.toggleAttribute('data-no-overflow', end <= 2);
      this.querySelector('[data-track-step="-1"]').disabled = this.track.scrollLeft <= 2;
      this.querySelector('[data-track-step="1"]').disabled = this.track.scrollLeft >= end - 2;
    }
    safePlay(video) {
      this.pauseOthers(video);
      return video.play().catch(() => {});
    }
    loadPreview(card) {
      let video = card.querySelector('video');
      if (video) return video;
      const template = card.querySelector('[data-video-template]');
      video = template.content.querySelector('video').cloneNode(true);
      if (template.dataset.previewSrc) {
        video.querySelectorAll('source').forEach(source => source.remove());
        video.src = template.dataset.previewSrc;
      }
      video.muted = true;
      video.defaultMuted = true;
      video.preload = 'none';
      video.loop = false;
      video.setAttribute('aria-hidden', 'true');
      video.tabIndex = -1;
      card.querySelector('.u5-feed__poster').after(video);
      ['play', 'pause', 'volumechange', 'ended'].forEach(type => this.listen(video, type, () => this.syncCard(card, video)));
      this.listen(video, 'ended', () => this.advancePreview(card));
      this.syncCard(card, video);
      return video;
    }
    syncPlay(button, video) {
      button.setAttribute('aria-label', video.paused ? this.labels.play : this.labels.pause);
      button.querySelector('[data-icon-play]').hidden = !video.paused;
      button.querySelector('[data-icon-pause]').hidden = video.paused;
    }
    syncCard(card, video) {
      card.dataset.playing = String(!video.paused);

    }
    pauseAll() {
      this.querySelectorAll('video').forEach(video => video.pause());
    }
    pauseOthers(current) {
      this.querySelectorAll('video').forEach(video => { if (video !== current && !video.paused) video.pause(); });
    }
    resumePreviews() {
      if (!this.autoEnabled || this.dialog.open || document.hidden || this.reducedMotion.matches) return;
      const candidates = this.cards.filter(card => this.visibleCards.has(card) && card.dataset.autoplay === 'true' && card.dataset.userPaused !== 'true');
      if (candidates.some(card => card.querySelector('video') && !card.querySelector('video').paused)) return;
      if (this.pendingPreview && !candidates.includes(this.pendingPreview)) return;
      const card = this.pendingPreview || candidates.find(card => this.cards.indexOf(card) === this.previewIndex) || candidates[0];
      if (card) {
        this.pendingPreview = null;
        this.previewIndex = this.cards.indexOf(card);
        const video = this.loadPreview(card);
        video.muted = true;
        this.safePlay(video);
      }
    }
    advancePreview(card) {
      if (!this.autoEnabled || this.dialog.open || document.hidden || !this.visibleCards.has(card)) return;
      const start = this.cards.indexOf(card);
      for (let offset = 1; offset <= this.cards.length; offset++) {
        const next = this.cards[(start + offset) % this.cards.length];
        if (next.dataset.autoplay !== 'true' || next.dataset.userPaused === 'true') continue;
        this.pendingPreview = next;
        this.previewIndex = this.cards.indexOf(next);
        const trackBox = this.track.getBoundingClientRect();
        const nextBox = next.getBoundingClientRect();
        if (nextBox.left < trackBox.left || nextBox.right > trackBox.right) {
          this.track.scrollTo({ left: next.offsetLeft - this.track.firstElementChild.offsetLeft, behavior: 'instant' });
        }
        this.resumePreviews();
        return;
      }
    }
    onClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-track-step')) {
        const step = this.track.firstElementChild.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).columnGap);
        this.track.scrollBy({ left: Number(button.dataset.trackStep) * step, behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
        return;
      }
      const card = button.closest('[data-video-card]');
      if (button.hasAttribute('data-open-video')) {
        this.opener = button;
        this.pauseAll();
        if (!this.dialog.open) {
          this.oldOverflow = document.documentElement.style.overflow;
          this.dialog.showModal();
          document.documentElement.style.overflow = 'hidden';
        }
        this.soundMuted = false;
        this.showVideo(this.cards.indexOf(card));
      } else if (button.hasAttribute('data-close-video')) this.dialog.close();
      else if (button.hasAttribute('data-dock-video')) this.setDocked(!this.isDocked);
      else if (button.hasAttribute('data-video-step')) this.showVideo(this.index + Number(button.dataset.videoStep));
      else if (button.hasAttribute('data-dialog-play')) {
        if (this.player.paused) this.safePlay(this.player);
        else this.player.pause();
      } else if (button.hasAttribute('data-dialog-sound')) this.player.muted = !this.player.muted;
      else if (button.hasAttribute('data-fullscreen')) this.fullscreen();
    }
    showVideo(index) {
      this.index = (index + this.cards.length) % this.cards.length;
      this.player?.pause();
      this.playerAbort?.abort();
      this.playerAbort = new AbortController();
      const card = this.cards[this.index];
      this.player = card.querySelector('template').content.querySelector('video').cloneNode(true);
      this.player.controls = true;
      this.player.setAttribute('controlslist', 'nofullscreen');
      this.player.removeAttribute('aria-hidden');
      this.player.setAttribute('aria-label', card.querySelector('[data-open-video]').getAttribute('aria-label'));
      this.player.muted = this.soundMuted;
      this.player.loop = false;
      this.previewIndex = this.index;
      this.player.preload = 'metadata';
      this.player.poster = card.querySelector('.u5-feed__poster').currentSrc || card.querySelector('.u5-feed__poster').src;
      this.host.style.setProperty('--u5-player-backdrop', `url(${JSON.stringify(this.player.poster)})`);
      this.host.replaceChildren(this.player);
      this.status.textContent = '';
      this.querySelector('[data-video-count]').textContent = `${this.index + 1} / ${this.cards.length}`;
      ['play', 'pause', 'volumechange', 'ended'].forEach(type => this.player.addEventListener(type, () => this.syncDialog(), { signal: this.playerAbort.signal }));
      this.player.addEventListener('ended', () => this.showVideo(this.index + 1), { signal: this.playerAbort.signal });
      this.player.addEventListener('error', () => { this.status.textContent = this.labels.unavailable; }, { signal: this.playerAbort.signal });
      this.syncDialog();
      this.safePlay(this.player);
    }
    syncDialog() {
      if (!this.player) return;
      this.syncPlay(this.querySelector('[data-dialog-play]'), this.player);
      const sound = this.querySelector('[data-dialog-sound]');
      this.soundMuted = this.player.muted;
      sound.setAttribute('aria-label', this.player.muted ? this.labels.unmute : this.labels.mute);
      sound.textContent = this.player.muted ? this.labels.soundOff : this.labels.soundOn;
    }
    async setDocked(docked) {
      if (!this.dialog.open || docked && this.mobile.matches) return;
      if (document.fullscreenElement === this.dialog) await document.exitFullscreen().catch(() => {});
      this.changingMode = true;
      this.dialog.close();
      this.dialog.classList.remove('u5-feed__dialog--expanded');
      this.dialog.classList.toggle('u5-feed__dialog--docked', docked);
      this.isDocked = docked;
      this.dialog.setAttribute('aria-modal', String(!docked));
      if (docked) {
        document.documentElement.style.overflow = this.oldOverflow || '';
        this.dialog.show();
      } else {
        this.oldOverflow = document.documentElement.style.overflow;
        this.dialog.showModal();
        document.documentElement.style.overflow = 'hidden';
      }
      const button = this.querySelector('[data-dock-video]');
      button.textContent = docked ? button.dataset.labelExpand : button.dataset.labelDock;
      this.changingMode = false;
    }
    async fullscreen() {
      if (this.isDocked) await this.setDocked(false);
      if (document.fullscreenElement === this.dialog) {
        document.exitFullscreen().catch(() => {});
        this.dialog.classList.remove('u5-feed__dialog--expanded');
        return;
      }
      if (this.dialog.classList.contains('u5-feed__dialog--expanded')) {
        this.dialog.classList.remove('u5-feed__dialog--expanded');
        return;
      }
      this.dialog.classList.add('u5-feed__dialog--expanded');
      if (this.dialog.requestFullscreen && document.fullscreenEnabled) this.dialog.requestFullscreen().catch(() => {});
    }
    onClose() {
      if (this.changingMode || this.dialog.open) return;
      if (document.fullscreenElement === this.dialog) document.exitFullscreen().catch(() => {});
      this.player?.pause();
      this.playerAbort?.abort();
      this.host.replaceChildren();
      this.player = null;
      this.dialog.classList.remove('u5-feed__dialog--expanded');
      this.dialog.classList.remove('u5-feed__dialog--docked');
      if (!this.isDocked) document.documentElement.style.overflow = this.oldOverflow || '';
      this.isDocked = false;
      this.querySelector('[data-dock-video]').textContent = this.querySelector('[data-dock-video]').dataset.labelDock;
      if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
      this.resumePreviews();
    }
  });
}





